<template>
  <footer
    v-if="store.totalQuantity || $cart.nonBundleItems.length"
    class="flex flex-col gap-2 px-4 py-2 shadow-top"
  >
    <!-- Non Bundle Checkout -->
    <template v-if="checkout.nonBundleOrder">
      <button
        class="button +green text-2xl"
        @click="checkout.redirectToCheckout"
      >
        Checkout
        -
        {{ formatCents(checkout.totalCents) }}
      </button>
    </template>

    <!-- Coffee Onetime only Checkout -->
    <template v-else-if="onetimeOnly">
      <DisclosureButton
        v-if="validSize"
        class="+green +large"
        :disabled="checkout.loading"
        data-gtm-category="bundle"
        data-gtm-action="checkout"
        data-gtm-label="onetime only"
        @click="checkout.addToCart()"
      >
        Proceed to Checkout
        <template #disclosure>
          {{ formatMoney(store.priceForSize(size)) }}
        </template>
      </DisclosureButton>
      <DisclosureButton
        v-else
        class="+gray +large"
        :disabled="isInPDPorPLP"
        @click.prevent="handleContinueShopping"
      >
        <span v-if="totalQuantity < store.MIN_SIZE">
          Please Select At Least 3
        </span>
        <span v-else>
          {{ $bt('choose_valid_size', store.bundleType) }}
        </span>
      </DisclosureButton>
      <BundleMiniCallouts />
    </template>

    <!-- Bundle Checkout -->
    <template v-else>
      <!-- Valid size -->
      <template v-if="validSize">
        <div class="text-center text-blue-500 font-semibold w-full text-sm">
          <template v-if="!isSub">
            {{ $t('bundler.save_with_subscription', { percentage: FIRST_TIME_PERCENT }) }}
          </template>
          <template v-else-if="size === 16">
            Add 8 packs & save $4.50
          </template>
          <template v-else-if="size === 24">
            Add 8 packs & save $5
          </template>
          <template v-else>
            {{ $t('bundler.you_are_saving', { percentage: FIRST_TIME_PERCENT }) }}
          </template>
        </div>

        <!-- Subscription purchase -->
        <template v-if="isSub">
          <DisclosureButton
            class="+green +large"
            :disabled="checkout.loading"
            data-gtm-category="bundle"
            data-gtm-action="checkout"
            data-gtm-label="subscription"
            @click="checkout.addToCart()"
          >
            {{ $t('bundler.go_to_checkout') }}
          </DisclosureButton>
          <BundleSummaryFooterCallouts />
        </template>

        <!-- Onetime Purchase -->
        <template v-else>
          <DisclosureButton
            class="+green"
            data-gtm-category="bundle"
            data-gtm-action="checkout"
            data-gtm-label="subscription switch"
            :disabled="checkout.loading"
            :value="$cart.subscriptionTotalPrice"
            @click="checkout.addToCart('subscription')"
          >
            {{ $t('bundler.checkout_with_subscription') }}
          </DisclosureButton>
          <BundleSummaryFooterCallouts />
          <DisclosureButton
            class="+border"
            data-gtm-category="bundle"
            data-gtm-action="checkout"
            data-gtm-label="one time"
            :disabled="checkout.loading"
            :value="$cart.onetimeTotalPrice"
            @click="onetimeConfirm = true"
          >
            {{ $t('bundler.continue_with_onetime') }}
          </DisclosureButton>
        </template>
      </template>

      <!-- Invalid Size -->
      <template v-else>
        <DisclosureButton
          class="+gray"
          :disabled="isInPDPorPLP"
          @click.prevent="handleContinueShopping"
        >
          <span v-if="totalQuantity < store.MIN_SIZE">
            {{ $bt('min_size_prompt', store.bundleType, { minSize: store.MIN_SIZE }) }}
          </span>
          <span v-else>
            {{ $bt('choose_valid_size', store.bundleType) }}
          </span>
        </DisclosureButton>
        <a
          :href="isCoffeeBundle ? '#product-bundler' : '/products/custom-bundle'"
          class="continue-shopping button"
          data-gtm-action="summary continue shopping"
          @click.prevent="handleContinueShopping"
        >
          {{ $t('action.continue_shopping') }}
        </a>
      </template>
    </template>
  </footer>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { formatCents, formatMoney } from '@/utils/formatters'
import { FIRST_TIME_PERCENT } from '@/utils/bundle'
import { useBundleStore, useBundleCheckoutStore, useThemeStore } from '@/stores'
import BundleMiniCallouts from './bundle-mini-callouts.vue'
import BundleSummaryFooterCallouts from './bundle-summary-footer-callouts.vue'
import DisclosureButton from './disclosure-button.vue'
const store = useBundleStore()
const checkout = useBundleCheckoutStore()
const { sheet, isInPDPorPLP } = useThemeStore()
const { isSub, isCoffeeBundle, totalQuantity, size, validSize, onetimeConfirm,onetimeOnly } = storeToRefs(store)

const handleContinueShopping = async () => {
  if (sheet.visible) {
    sheet.close()
  }
  if (!isInPDPorPLP && !window.location.href.includes('coffee')) {
    window.location.href = '/products/custom-bundle'
  }
}

onMounted(() => checkout.loading = false)
</script>
